import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import useFetchInterval from "src/ui/hooks/useFetchInterval";
import useTableFetchLoading from "src/ui/hooks/useTableFetchLoading";
import { SecondaryButton } from "src/ui/shared/CustomButton";
import { FilterHeader } from "src/ui/shared/table/headers/FilterHeader";
import { CustomTableHeader } from "src/ui/shared/table/TableHeader";
import { makeStyles } from "tss-react/mui";

import { useCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { GetFilesParams } from "@dashboard/files/api/rtkClient";
import { SourceType } from "@dashboard/files/types";
import { hasAnalyzerInProgress } from "@dashboard/files/utils";
import { TagFilter } from "@dashboard/shared/components/TagFilter";
import { Box, TableRow, Typography } from "@mui/material";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { Tooltip } from "@shared/Tooltip";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useFileUpload } from "../../hooks/useFileUpload";
import { TableItem } from "../TableItem/TableItem";
import { getLastTableRowStyle } from "../TableItem/TableItem.style";

import { useFilesWarnings } from "./useFilesWarnings/useFilesWarnings";
import { FileDropOverlay } from "./FileDropOverlay";
import { FileExtendedTableItem, tableColumns, useParams, useTransformFilesToTableData } from "./UploadTable.utils";

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
        flexShrink: 0,
    },
}));

export type FilesTableParams = Required<
    Pick<
        GetFilesParams,
        | "workspaceId"
        | "pageIndex"
        | "pageSize"
        | "sortCriteria"
        | "sortDirection"
        | "groups"
        | "uploaders"
        | "sourceTypes"
        | "includeParentFiles"
        | "filename"
    >
>;

type Props = {
    selectedGroupId?: number | null;
    includeParentFiles?: boolean;
    dropDisabled?: boolean;
    dropDisabledReason?: string;
    onUploadClick: () => void;
};

const UploadTable = (props: Props) => {
    const [params, setParams] = useParams();

    const { classes, cx } = useStyles();
    const { classes: layoutClasses } = useFullPageTableLayoutStyles();

    const { t } = useTranslation(["pages", "common"]);

    const { data, refetch, isFetching, isLoading } = useCurrentWorkspaceFiles(params);
    const files = React.useMemo(() => data?.list ?? [], [data?.list]);
    const tableLoading = useTableFetchLoading(isFetching, isLoading, params);
    const transformedFiles = useTransformFilesToTableData(files);
    const dataWithWarnings = useFilesWarnings(transformedFiles);

    const { onFilesSelected } = useFileUpload({ groupId: props.selectedGroupId ?? undefined });

    const onSort = (id: string) => {
        setParams((prev) => ({
            ...prev,
            sortCriteria: id,
            sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
            pageIndex: 0,
        }));
    };

    const onSearch = (value: string) => {
        setParams((prev) => ({ ...prev, filename: value, pageIndex: 0 }));
    };

    const onSourceTypesFilter = (_values: Record<string, boolean>) => {
        const arr = Object.keys(_.pickBy(_values, (item) => item));
        const value = arr.map((item) => (item === "device" ? SourceType.Device : SourceType.User));
        setParams({ ...params, sourceTypes: value, pageIndex: 0 });
    };

    const table = useReactTable<FileExtendedTableItem>({
        data: dataWithWarnings,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        meta: { selectedGroupId: props.selectedGroupId ?? undefined, searchQuery: params.filename },
    });

    // The table body always fills the available height and scrolls internally only when the rows
    // actually overflow it, so the search bar stays on top and the pagination controls stay pinned
    // at the bottom regardless of page size. No filler rows: a short page just leaves empty space.
    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [
        params.pageIndex,
        params.pageSize,
        files.length,
    ]);

    const handleResetFilter = () => {
        setParams((prev) => ({ ...prev, filename: "", sourceTypes: [], pageIndex: 0 }));
    };

    useFetchInterval(refetch, { condition: hasAnalyzerInProgress(files) });

    React.useEffect(() => {
        const groupId = props.selectedGroupId;
        if (groupId === undefined) {
            return;
        }
        if (groupId === null) {
            setParams((prev) => ({ ...prev, groups: [], pageIndex: 0, includeParentFiles: false }));
        } else {
            setParams((prev) => ({
                ...prev,
                groups: [groupId],
                pageIndex: 0,
                includeParentFiles: props.includeParentFiles ?? false,
            }));
        }
    }, [props.selectedGroupId, props.includeParentFiles]);

    const emptyMessage = params.filename.trim()
        ? t("pages:filesPage.table.noSearchResults", { query: params.filename.trim() })
        : t("pages:filesPage.input.uploadField.placeholder");

    return (
        <Box className={layoutClasses.tableLayout}>
            <div className={classes.searchContainer}>
                <QueryBuilderField
                    name="file-search"
                    autoComplete="file-search-field"
                    placeholder={t("common:button.search")}
                    value={params.filename}
                    onChange={onSearch}
                    onClear={() => onSearch("")}
                    loading={tableLoading}
                />
                <Tooltip title={props.dropDisabledReason} hide={!props.dropDisabled}>
                    <SecondaryButton
                        onClick={props.onUploadClick}
                        disabled={props.dropDisabled}
                        startIcon={<i className="fa-solid fa-upload" />}
                        size="small"
                        name="upload-file"
                    >
                        <Typography>{t("common:button.upload")}</Typography>
                    </SecondaryButton>
                </Tooltip>
            </div>
            <FileDropOverlay
                className={layoutClasses.tableWrapper}
                onFiles={onFilesSelected}
                disabled={props.dropDisabled}
                disabledReason={props.dropDisabledReason}
            >
                <MinimalTableContainer
                    ref={tableContainerRef}
                    className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                >
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <CustomTableHeader
                                                key={header.id}
                                                sortCriteria={params.sortCriteria}
                                                sortDirection={params.sortDirection}
                                                onSort={onSort}
                                                {...header}
                                            >
                                                {/* TODO: fill out TagFilter after the tag filtering is implemented on the backend. */}
                                                {header.id === "filesTags" ? (
                                                    <TagFilter tags={[]} filters={[]} onChange={() => {}} />
                                                ) : null}

                                                {header.id === "ownerId" ? (
                                                    <FilterHeader
                                                        title={t("pages:filesPage.table.filters.uploaderFilter")}
                                                        sortDisabled
                                                        onSubmit={onSourceTypesFilter}
                                                        options={[
                                                            { key: "user", label: t("pages:filesPage.table.filters.userFiles") },
                                                            {
                                                                key: "device",
                                                                label: t("pages:filesPage.table.filters.deviceFiles"),
                                                            },
                                                        ]}
                                                        initialValues={{
                                                            user: params.sourceTypes.includes(SourceType.User),
                                                            device: params.sourceTypes.includes(SourceType.Device),
                                                        }}
                                                    />
                                                ) : null}
                                            </CustomTableHeader>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody<FileExtendedTableItem>
                            table={table}
                            filter={{
                                isActive: !!params.filename || !!params.sourceTypes.length,
                                onResetFilter: handleResetFilter,
                            }}
                            noDataMessage={emptyMessage}
                            loading={tableLoading}
                        >
                            {({ rows }) =>
                                rows.map((row, index) => (
                                    <TableItem key={row.id} row={row} style={getLastTableRowStyle(index, rows.length)} />
                                ))
                            }
                        </TableBody>
                    </MinimalTable>
                </MinimalTableContainer>
            </FileDropOverlay>

            <CustomTablePagination
                component="div"
                className={layoutClasses.pagination}
                rowsPerPageOptions={[12, 24, 48]}
                count={data?.count ?? 0}
                rowsPerPage={params.pageSize}
                page={params.pageIndex}
                onPageChange={(_event, page) => {
                    setParams({ ...params, pageIndex: page });
                }}
                onRowsPerPageChange={(e) => {
                    setParams({ ...params, pageSize: +e.target.value });
                }}
            />
        </Box>
    );
};

export default UploadTable;
